import type { ReviewItem, SignalDefinition } from "@ti/core";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, Check, CircleHelp, ListChecks, X } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { PrecisionBadge, SignalTierBadge } from "@/components/domain/analytics";
import { TimeAgo } from "@/components/domain/misc";
import { ErrorPanel } from "@/components/layout/page";
import { Badge, type BadgeTone } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { Select } from "@/components/ui/input";
import { EmptyState, Kbd, Meter, Skeleton } from "@/components/ui/misc";
import { SessionLink } from "@/features/analytics-shared/links";
import { DEFAULT_THRESHOLD, MethodBadge } from "@/features/signals/meta";
import { q, useLabelReviewItem } from "@/lib/api";
import { fmtPct } from "@/lib/format";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/signals/review")({
  validateSearch: z.object({ definitionId: z.string().min(1).optional().catch(undefined) }),
  component: ReviewPage,
});

type Label = NonNullable<ReviewItem["label"]>;

const LABEL_META: Record<Label, { text: string; tone: BadgeTone; icon: ReactNode; key: string }> = {
  correct: { text: "Correct", tone: "good", icon: <Check />, key: "c" },
  incorrect: { text: "Incorrect", tone: "critical", icon: <X />, key: "x" },
  unsure: { text: "Unsure", tone: "neutral", icon: <CircleHelp />, key: "u" },
};

function LabelBadge({ label }: { label: Label }) {
  const m = LABEL_META[label];
  return (
    <Badge tone={m.tone}>
      {m.icon}
      {m.text}
    </Badge>
  );
}

function isTyping(t: EventTarget | null) {
  const el = t as HTMLElement | null;
  return !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable);
}

function ReviewPage() {
  const { definitionId } = Route.useSearch();
  const navigate = Route.useNavigate();
  const queue = useQuery(q.reviewQueue());
  const defs = useQuery(q.signalDefinitions());
  const label = useLabelReviewItem();
  const defById = useMemo(() => new Map(defs.data?.map((d) => [d.id, d])), [defs.data]);

  // Optimistic labels so the reviewer can move on without waiting for the round trip.
  const [local, setLocal] = useState<Record<string, Label>>({});
  const labelOf = useCallback((i: ReviewItem): Label | null => local[i.id] ?? i.label, [local]);

  const items = useMemo(() => (definitionId ? queue.data?.filter((i) => i.definitionId === definitionId) : queue.data), [queue.data, definitionId]);

  // The pass: pending items when the filter was chosen, in a stable order (labeling doesn't reshuffle it).
  const [order, setOrder] = useState<string[]>([]);
  const [pos, setPos] = useState(0);
  const [doneInPass, setDoneInPass] = useState(0);
  useEffect(() => {
    setOrder([]);
    setPos(0);
    setDoneInPass(0);
  }, [definitionId]);
  useEffect(() => {
    if (!items) return;
    setOrder((prev) => {
      const fresh = items.filter((i) => i.label === null && !local[i.id] && !prev.includes(i.id)).map((i) => i.id);
      return fresh.length ? [...prev, ...fresh] : prev;
    });
  }, [items, local]);

  const byId = useMemo(() => new Map(queue.data?.map((i) => [i.id, i])), [queue.data]);
  const current = order[pos] ? byId.get(order[pos]!) : undefined;
  const pending = items?.filter((i) => !labelOf(i)).length ?? 0;
  const history = useMemo(() => (items ?? []).filter((i) => labelOf(i)).sort((a, b) => Number(!!local[b.id]) - Number(!!local[a.id]) || b.createdAt.localeCompare(a.createdAt)), [items, labelOf, local]);

  const go = useCallback((delta: number) => setPos((p) => Math.min(Math.max(0, p + delta), Math.max(0, order.length - 1))), [order.length]);

  const apply = useCallback(
    (l: Label) => {
      if (!current) return;
      const first = !labelOf(current);
      setLocal((m) => ({ ...m, [current.id]: l }));
      if (first) setDoneInPass((n) => n + 1);
      label.mutate(
        { id: current.id, label: l },
        {
          onError: () =>
            setLocal((m) => {
              const { [current.id]: _drop, ...rest } = m;
              return rest;
            }),
        },
      );
      // Advance to the next item in the pass that still needs a label.
      const next = order.findIndex((id, i) => i > pos && !local[id] && byId.get(id)?.label === null);
      setPos(next >= 0 ? next : Math.min(pos + 1, order.length));
    },
    [current, labelOf, label, order, pos, local, byId],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isTyping(e.target) || e.metaKey || e.ctrlKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (k === "c") apply("correct");
      else if (k === "x") apply("incorrect");
      else if (k === "u") apply("unsure");
      else if (k === "j") go(1);
      else if (k === "k") go(-1);
      else return;
      e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [apply, go]);

  const filterOptions = useMemo(() => {
    const m = new Map<string, { name: string; pending: number; total: number }>();
    for (const i of queue.data ?? []) {
      const e = m.get(i.definitionId) ?? { name: i.signalName, pending: 0, total: 0 };
      e.total++;
      if (!labelOf(i)) e.pending++;
      m.set(i.definitionId, e);
    }
    return [...m].sort((a, b) => b[1].pending - a[1].pending);
  }, [queue.data, labelOf]);

  const jumpTo = (id: string) => {
    const i = order.indexOf(id);
    if (i >= 0) setPos(i);
    else {
      setOrder((o) => [...o, id]);
      setPos(order.length);
    }
  };

  if (queue.error) return <ErrorPanel error={queue.error} reset={() => void queue.refetch()} />;

  const passTotal = order.length;
  const finished = !!items && pos >= order.length;

  return (
    <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
      <div className="flex min-w-0 flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Select aria-label="Filter by signal" value={definitionId ?? ""} onChange={(e) => void navigate({ search: { definitionId: e.target.value || undefined }, replace: true })} className="w-80">
            <option value="">All signals · {queue.data?.filter((i) => !labelOf(i)).length ?? 0} to review</option>
            {definitionId && !filterOptions.some(([id]) => id === definitionId) ? <option value={definitionId}>{defById.get(definitionId)?.name ?? definitionId}</option> : null}
            {filterOptions.map(([id, o]) => (
              <option key={id} value={id}>
                {o.name} · {o.pending} to review
              </option>
            ))}
          </Select>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={() => go(-1)} disabled={pos === 0} aria-keyshortcuts="k">
              <ArrowLeft /> Prev <Kbd>k</Kbd>
            </Button>
            <span className="px-1 text-xs tabular text-fg-3" aria-live="polite">
              {passTotal ? `${Math.min(pos + 1, passTotal)} / ${passTotal}` : "0 / 0"}
            </span>
            <Button variant="ghost" size="sm" onClick={() => go(1)} disabled={pos >= passTotal - 1} aria-keyshortcuts="j">
              Next <Kbd>j</Kbd> <ArrowRight />
            </Button>
          </div>
        </div>

        {!items ? (
          <Skeleton className="h-[360px]" />
        ) : current && !finished ? (
          <ReviewCard item={current} def={defById.get(current.definitionId)} label={labelOf(current)} onLabel={apply} pending={label.isPending} />
        ) : (
          <Card>
            <EmptyState
              icon={<ListChecks />}
              title={items.length ? "Queue clear" : "Nothing to review"}
              description={
                items.length
                  ? `You've labeled every hit${definitionId ? " for this signal" : ""} in this pass. New shadow hits and a sample of active hits arrive as sessions close.`
                  : "Hits from shadow signals and a sample of active hits land here for labeling."
              }
              action={
                definitionId ? (
                  <Button size="sm" variant="outline" className="mt-1" onClick={() => void navigate({ search: {}, replace: true })}>
                    Review all signals
                  </Button>
                ) : null
              }
            />
          </Card>
        )}
      </div>

      <div className="flex flex-col gap-3">
        <Card>
          <CardHeader title="Progress" description={`${pending} awaiting review${definitionId ? " for this signal" : ""}`} />
          <CardBody className="flex flex-col gap-3">
            <div>
              <div className="mb-1 flex justify-between text-xs">
                <span className="text-fg-2">This pass</span>
                <span className="tabular text-fg-3">
                  {doneInPass} of {passTotal} labeled
                </span>
              </div>
              <Meter value={doneInPass} max={Math.max(1, passTotal)} label="Labeled in this pass" />
            </div>
            <dl className="grid grid-cols-3 gap-2 text-center">
              {(["correct", "incorrect", "unsure"] as const).map((l) => (
                <div key={l} className="rounded-md border bg-surface-2 px-2 py-1.5">
                  <dt className="text-[11px] text-fg-3">{LABEL_META[l].text}</dt>
                  <dd className="text-[15px] font-semibold tabular">{history.filter((i) => labelOf(i) === l).length}</dd>
                </div>
              ))}
            </dl>
            <div className="border-t pt-3 text-xs text-fg-3">
              <p className="mb-1.5 font-medium text-fg-2">Keyboard</p>
              <ul className="grid grid-cols-2 gap-x-3 gap-y-1">
                <li><Kbd>c</Kbd> correct</li>
                <li><Kbd>x</Kbd> incorrect</li>
                <li><Kbd>u</Kbd> unsure</li>
                <li><Kbd>j</Kbd> / <Kbd>k</Kbd> next / prev</li>
              </ul>
              <p className="mt-2">Unsure labels don't count towards precision. Labels become examples, calibration data and CI evaluation cases.</p>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Labeled" description="Most recent first · select to change a label" />
          {history.length ? (
            <ul className="scrollbar-thin max-h-[420px] divide-y overflow-y-auto">
              {history.map((i) => (
                <li key={i.id}>
                  <button type="button" onClick={() => jumpTo(i.id)} className={cn("flex w-full cursor-pointer flex-col gap-1 px-4 py-2 text-left hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-none", current?.id === i.id && "bg-surface-2")}>
                    <span className="flex items-center justify-between gap-2">
                      <span className="truncate text-xs font-medium text-fg">{i.signalName}</span>
                      <LabelBadge label={labelOf(i)!} />
                    </span>
                    <span className="truncate text-xs text-fg-3">{i.excerpt}</span>
                    <span className="text-[11px] text-fg-3">{local[i.id] ? "you · just now" : (i.labeledBy ?? "—")}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title="No labels yet" className="py-8" />
          )}
        </Card>
      </div>
    </div>
  );
}

function ReviewCard({ item, def, label, onLabel, pending }: { item: ReviewItem; def: SignalDefinition | undefined; label: Label | null; onLabel: (l: Label) => void; pending: boolean }) {
  const p = item.confidence;
  // The hit's own threshold when the API recorded it; else the definition's current one.
  const t = def?.threshold ?? DEFAULT_THRESHOLD;
  const near = Math.abs(p - t) < 0.08;
  return (
    <Card>
      <CardHeader
        title={
          def ? (
            <Link to="/signals/studio/$definitionId" params={{ definitionId: def.id }} className="hover:text-accent hover:underline">
              {item.signalName}
            </Link>
          ) : (
            item.signalName
          )
        }
        description={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-mono text-[11.5px] text-fg-2">{item.agentName}</span>
            <span aria-hidden>·</span>
            <SessionLink id={item.sessionId} />
            <span aria-hidden>·</span>
            <TimeAgo iso={item.createdAt} className="text-fg-3" />
          </span>
        }
        actions={
          <span className="flex items-center gap-2">
            {def ? <MethodBadge method={def.method} /> : null}
            {def ? <SignalTierBadge tier={def.tierClass} /> : null}
            {label ? <LabelBadge label={label} /> : null}
          </span>
        }
      />
      <CardBody className="flex flex-col gap-4">
        {def ? <p className="text-xs text-fg-3">{def.description}</p> : null}
        <figure>
          <figcaption className="mb-1 text-[11px] font-medium uppercase tracking-wide text-fg-3">Excerpt</figcaption>
          <blockquote className="whitespace-pre-line rounded-md border-l-2 border-accent bg-surface-2 px-3 py-2.5 text-[14px] leading-relaxed text-fg">{item.excerpt}</blockquote>
        </figure>
        <div>
          <h4 className="mb-1 text-[11px] font-medium uppercase tracking-wide text-fg-3">Model explanation</h4>
          <p className="text-[13px] text-fg-2">{item.explanation}</p>
        </div>
        <div>
          <div className="mb-1 flex items-baseline justify-between text-xs">
            <span className="text-fg-2">
              Probability <span className="font-semibold tabular text-fg">{p.toFixed(2)}</span>
            </span>
            <span className="text-fg-3 tabular">threshold {t.toFixed(2)}{def ? (def.thresholdSource === "calibrated" ? " (tuned)" : def.thresholdSource === "manual" ? " (set by hand)" : " (default until tuned)") : ""}</span>
          </div>
          <div className="relative h-2 rounded-full bg-surface-3" role="meter" aria-valuenow={p} aria-valuemin={0} aria-valuemax={1} aria-label={`Probability ${p.toFixed(2)} against threshold ${t.toFixed(2)}`}>
            <div className="h-full rounded-full" style={{ width: `${p * 100}%`, background: "var(--seq-400)" }} />
            <span className="absolute -top-1 h-4 w-0.5 rounded bg-fg" style={{ left: `${t * 100}%` }} aria-hidden />
          </div>
          <p className="mt-1.5 text-xs text-fg-3">
            {near ? "Near the threshold: labels here move the threshold the most." : p > t ? "Clearly above the threshold; part of the random sample that keeps the precision estimate unbiased." : "Below the threshold."}
          </p>
        </div>
        {def ? (
          <p className="flex items-center gap-2 text-xs text-fg-3">
            Precision so far <PrecisionBadge value={def.precision} labeled={def.labeled} />
          </p>
        ) : null}
        <div className="flex flex-wrap items-center gap-2 border-t pt-4">
          {(["correct", "incorrect", "unsure"] as const).map((l) => {
            const m = LABEL_META[l];
            return (
              <Button key={l} variant={label === l ? "default" : "outline"} onClick={() => onLabel(l)} disabled={pending && label === l} aria-keyshortcuts={m.key} aria-pressed={label === l}>
                {m.icon} {m.text} <Kbd>{m.key}</Kbd>
              </Button>
            );
          })}
          <span className="ml-auto text-xs text-fg-3">{fmtPct(p)} confident · {item.id}</span>
        </div>
      </CardBody>
    </Card>
  );
}
